import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { paletteByKey } from '../theme/blogPalette';

/* All blog content (posts + categories) lives in Firestore and is
   managed entirely through the separate /admin panel — nothing here
   is hardcoded post data, just the read layer the public site uses. */

function decorateCategory(id, data) {
  const palette = paletteByKey(data.paletteKey);
  return {
    key: id,
    label: data.label || id,
    accent: palette.accent,
    rgb: palette.rgb,
    image: data.image || '',
  };
}

function decoratePost(id, data, categoriesByKey) {
  const cat = categoriesByKey[data.categoryKey] || {
    key: data.categoryKey || 'uncategorized',
    label: data.categoryKey || 'Uncategorized',
    accent: 'var(--ink)',
    rgb: '10,10,20',
    image: '',
  };
  return {
    slug: id,
    title: data.title || '',
    excerpt: data.excerpt || '',
    author: data.author || '',
    role: data.role || '',
    date: data.date || '',
    readTime: data.readTime || '',
    featured: !!data.featured,
    published: data.published !== false,
    body: Array.isArray(data.body) ? data.body : [],
    related: Array.isArray(data.relatedSlugs) ? data.relatedSlugs : [],
    category: data.categoryKey,
    categoryLabel: cat.label,
    accent: cat.accent,
    rgb: cat.rgb,
    image: data.image || cat.image,
  };
}

function sortByDateDesc(a, b) {
  return new Date(b.date) - new Date(a.date);
}

/* ── Categories ── */

export function subscribeCategories(callback) {
  return onSnapshot(collection(db, 'categories'), (snap) => {
    const cats = snap.docs.map((d) => decorateCategory(d.id, d.data()));
    callback(cats);
  });
}

export async function getCategoriesOnce() {
  const snap = await getDocs(collection(db, 'categories'));
  return snap.docs.map((d) => decorateCategory(d.id, d.data()));
}

/* ── Posts ── */

export function subscribePublishedPosts(callback) {
  const q = query(collection(db, 'posts'), where('published', '==', true));
  return onSnapshot(q, async (snap) => {
    const categories = await getCategoriesOnce();
    const categoriesByKey = Object.fromEntries(categories.map((c) => [c.key, c]));
    const posts = snap.docs
      .map((d) => decoratePost(d.id, d.data(), categoriesByKey))
      .sort(sortByDateDesc);
    callback(posts);
  });
}

export async function getPublishedPostsOnce() {
  const categories = await getCategoriesOnce();
  const categoriesByKey = Object.fromEntries(categories.map((c) => [c.key, c]));
  const q = query(collection(db, 'posts'), where('published', '==', true));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => decoratePost(d.id, d.data(), categoriesByKey))
    .sort(sortByDateDesc);
}

export async function getPostBySlug(slug) {
  const categories = await getCategoriesOnce();
  const categoriesByKey = Object.fromEntries(categories.map((c) => [c.key, c]));
  const snap = await getDoc(doc(db, 'posts', slug));
  if (!snap.exists()) return null;
  const post = decoratePost(snap.id, snap.data(), categoriesByKey);
  return post.published ? post : null;
}

export function getRelatedPosts(post, allPosts, count = 3) {
  if (!post) return [];
  const bySlug = Object.fromEntries(allPosts.map((p) => [p.slug, p]));
  const explicit = (post.related || []).map((slug) => bySlug[slug]).filter(Boolean);
  if (explicit.length >= count) return explicit.slice(0, count);
  const fallback = allPosts.filter((p) => p.slug !== post.slug && !explicit.includes(p));
  return [...explicit, ...fallback].slice(0, count);
}
